/**
 * Impression tracker — shared across sections.
 *
 * Usage (any section):
 *   <div data-impression-track
 *        data-impression-event="HomepageBanner Impression"   // customer-event name (required)
 *        data-impression-name="summer-sale-banner"           // what was seen (required)
 *        data-impression-section="{{ section.id }}"          // optional
 *        data-impression-position="2"                        // optional, e.g. slide/tile index
 *        data-impression-threshold="0.5">                    // optional, default 0.5 (= 50% visible)
 *   </div>
 *
 * Fires Shopify.analytics.publish(eventName, payload) ONCE per unique
 * event+section+name per page load, when the element is >= threshold visible.
 *
 * Manual API (for sections that render late / via JS):
 *   window.ImpressionTracker.observe(rootElement)   // scan root for new [data-impression-track]
 *   window.ImpressionTracker.reset()                 // clear fired keys (rarely needed)
 */
(function () {
  if (window.ImpressionTracker) return;

  let SELECTOR = '[data-impression-track]';
  let fired = new Set();
  let observed = new WeakSet();
  let observers = {}; // threshold -> IntersectionObserver

  function publish(eventName, payload) {
    if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
      Shopify.analytics.publish(eventName, payload);
    } else if (window.console && console.debug) {
      console.debug('[ImpressionTracker] Shopify.analytics not ready', eventName, payload);
    }
  }

  function keyFor(el) {
    return [
      el.getAttribute('data-impression-event') || '',
      el.getAttribute('data-impression-section') || '',
      el.getAttribute('data-impression-name') || ''
    ].join('|');
  }

  function isSwiperDuplicate(el) {
    // Swiper (< v11) clones first/last slides for loop mode — don't count those.
    return el.classList.contains('swiper-slide-duplicate');
  }

  function fire(el) {
    let eventName = el.getAttribute('data-impression-event');
    let name = el.getAttribute('data-impression-name');
    if (!eventName || !name) return;

    let key = keyFor(el);
    if (fired.has(key)) return;
    fired.add(key);

    publish(eventName, {
      impression_name: name,
      section_id: el.getAttribute('data-impression-section') || ''
    });
  }

  function getObserver(threshold) {
    let t = String(threshold);
    if (observers[t]) return observers[t];

    observers[t] = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || entry.intersectionRatio < threshold) return;
        let el = entry.target;
        if (isSwiperDuplicate(el)) return;
        fire(el);
        // Once a unique key has fired, nothing more to do for this element.
        if (fired.has(keyFor(el))) obs.unobserve(el);
      });
    }, { threshold: [threshold] });

    return observers[t];
  }

  function observeElement(el) {
    if (observed.has(el)) return;
    observed.add(el);

    // Skip if this key already fired (e.g. a duplicate slide rendered later).
    if (fired.has(keyFor(el))) return;

    let threshold = parseFloat(el.getAttribute('data-impression-threshold'));
    if (isNaN(threshold) || threshold <= 0 || threshold > 1) threshold = 0.5;

    getObserver(threshold).observe(el);
  }

  function observe(root) {
    root = root || document;
    if (root.nodeType === 1 && root.matches && root.matches(SELECTOR)) observeElement(root);
    let nodes = root.querySelectorAll ? root.querySelectorAll(SELECTOR) : [];
    for (let i = 0; i < nodes.length; i++) observeElement(nodes[i]);
  }

  function init() {
    if (!('IntersectionObserver' in window)) return;
    observe(document);

    // Pick up elements added later (Swiper loop clones, section re-renders, lazy sections).
    let mo = new MutationObserver(function (mutations) {
      for (let i = 0; i < mutations.length; i++) {
        let added = mutations[i].addedNodes;
        for (let j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) observe(added[j]);
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // Theme editor: re-scan when a section is (re)loaded.
    document.addEventListener('shopify:section:load', function (e) {
      observe(e.target);
    });
  }

  window.ImpressionTracker = {
    observe: observe,
    reset: function () { fired.clear(); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();